import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { useSnackbar } from "../context/SnackbarContext";
import { api } from "../services/api";
import {
  DASHBOARD_QUERY_KEY,
  PLANNERS_QUERY_KEY,
  forViewer,
  plannerQuery,
  plannersQuery,
  viewerIdOf,
} from "../services/queries";
import {
  changedPlannerEventMessage,
  eventCountLabel,
  plannerAddedEventRequest,
  plannerEventPayloads,
  plannerRowProblemsFromServer,
  plannerSendFailureMessage,
  type PlannerDraft,
  type PlannerEventWhenAndWhere,
  type PlannerRow,
  type PlannerRowProblems,
  type SavedPlanner,
} from "../domain/planners/planner";
import type { ApiRequestError } from "../services/api/client";
import type { Notice } from "../services/api/types";
import { failureDetail } from "./useCertificateTemplates";
import { useFeatureFlags } from "./useFeatureFlags";

/** Every Planner, Draft or Sent. Not fetched while Planner is turned off. */
export const usePlanners = () => {
  const { user, token } = useAuth();
  const { isPlannerEnabled } = useFeatureFlags();
  return useQuery({
    ...forViewer(plannersQuery(user?.schoolId, token), viewerIdOf(user)),
    enabled: isPlannerEnabled && !!token && !!user?.schoolId,
  });
};

export const usePlanner = (plannerId: string | undefined) => {
  const { user, token } = useAuth();
  const { isPlannerEnabled } = useFeatureFlags();
  return useQuery({
    ...forViewer(plannerQuery(user?.schoolId, token, plannerId), viewerIdOf(user)),
    enabled: isPlannerEnabled && !!token && !!user?.schoolId && !!plannerId,
  });
};

/**
 * A Planner's status from the cached Planners list, if it's there, so its
 * page can show the right loading shape before the Planner itself arrives.
 */
export const useCachedPlannerStatus = (plannerId: string | undefined) => {
  const queryClient = useQueryClient();
  if (!plannerId) return undefined;
  return queryClient
    .getQueriesData<unknown>({ queryKey: PLANNERS_QUERY_KEY })
    .flatMap(([, data]) => (Array.isArray(data) ? (data as SavedPlanner[]) : []))
    .find((planner) => planner.plannerId === plannerId)?.status;
};

/** A Planner mutation that refreshes the Planners and shows a toast if it fails. */
const usePlannerWrite = <Variables, Result>(
  write: (variables: Variables, orgId: string, token: string) => Promise<Result>,
  failureMessage: string,
) => {
  const { user, token } = useAuth();
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: Variables) => {
      if (!token || !user?.schoolId) throw new Error("No token");
      return write(variables, user.schoolId, token);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PLANNERS_QUERY_KEY }),
    onError: (error) => {
      console.error(failureMessage, error);
      showMessage({ message: failureMessage + failureDetail(error), severity: "error" });
    },
  });
};

/** Saves a Draft: creates it the first time, then updates it. */
export const useSavePlannerDraft = () =>
  usePlannerWrite(
    async (
      {
        plannerId,
        draft,
      }: {
        /** Missing until the first save. */ plannerId?: string;
        draft: PlannerDraft;
      },
      orgId,
      token,
    ) => {
      const response = plannerId
        ? await api.planners.updatePlanner(orgId, plannerId, draft, token)
        : await api.planners.createPlanner(orgId, draft, token);
      return response.planner;
    },
    "Couldn't save the Planner.",
  );

/** Renames a sent Planner; the Planners list and the Planner refresh. */
export const useRenamePlanner = () =>
  usePlannerWrite(
    async ({ plannerId, title }: { plannerId: string; title: string }, orgId, token) =>
      (await api.planners.renamePlanner(orgId, plannerId, title, token)).planner,
    "Couldn't rename the Planner.",
  );

/** Forgets a deleted Planner, so refreshing the list doesn't refetch it (that would 404). */
const forgetPlanner = (
  queryClient: QueryClient,
  schoolId: string | undefined,
  token: string | null | undefined,
  plannerId: string,
) => {
  queryClient.removeQueries({ queryKey: plannerQuery(schoolId, token, plannerId).queryKey });
  void queryClient.invalidateQueries({ queryKey: PLANNERS_QUERY_KEY });
};

/** Drops deleted Events from every cached Notices list straight away; the refetch confirms it. */
const dropDeletedNotices = (queryClient: QueryClient, noticeIds: string[]) => {
  const deleted = new Set(noticeIds);
  queryClient.setQueriesData({ queryKey: ["notices"] }, (cached: unknown) =>
    Array.isArray(cached)
      ? cached.filter((notice: Notice) => !deleted.has(notice.noticeId || notice.id))
      : cached,
  );
};

/** Deletes a Draft Planner. */
export const useDeletePlanner = () => {
  const { user, token } = useAuth();
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (plannerId: string) => {
      if (!token || !user?.schoolId) throw new Error("No token");
      return api.planners.deletePlanner(user.schoolId, plannerId, token);
    },
    onSuccess: (_result, plannerId) => {
      forgetPlanner(queryClient, user?.schoolId, token, plannerId);
      showMessage({ message: "Planner deleted", severity: "success" });
    },
    onError: (error) => {
      console.error("Failed to delete Planner:", error);
      showMessage({
        message: "Couldn't delete the Planner." + failureDetail(error),
        severity: "error",
      });
    },
  });
};

/**
 * Delete all on a sent Planner: every Event in it (off parents' Calendars, Event
 * reminders cancelled) and the Planner itself. With no Events left, just the Planner.
 */
export const useDeleteSentPlanner = () => {
  const { user, token } = useAuth();
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      plannerId,
      anyEventId,
    }: {
      plannerId: string;
      /** Any one of its Events, or undefined when it has none left. */
      anyEventId?: string;
    }): Promise<string[]> => {
      if (!token || !user?.schoolId) throw new Error("No token");
      if (!anyEventId) {
        await api.planners.deletePlanner(user.schoolId, plannerId, token);
        return [];
      }
      const result = await api.notices.delete(anyEventId, user.schoolId, token, {
        scope: "planner",
      });
      return result.deletedNoticeIds ?? [anyEventId];
    },
    onSuccess: (deletedNoticeIds, { plannerId }) => {
      forgetPlanner(queryClient, user?.schoolId, token, plannerId);
      dropDeletedNotices(queryClient, deletedNoticeIds);
      refreshAfterPlannerEvents(queryClient);
      showMessage({
        message:
          deletedNoticeIds.length > 0
            ? `Planner and its ${eventCountLabel(deletedNoticeIds.length)} deleted`
            : "Planner deleted",
        severity: "success",
      });
    },
    onError: (error) => {
      console.error("Failed to delete the Planner and its Events:", error);
      // Some Events may have gone before it failed; show what's left.
      refreshAfterPlannerEvents(queryClient);
      showMessage({
        message:
          "Couldn't delete the Planner. Some of its Events may already be deleted; try again to delete the rest." +
          failureDetail(error),
        severity: "error",
      });
    },
  });
};

/** Deletes one Event in a Planner. The Planner stays, even with no Events left. */
export const useDeletePlannerEvent = () => {
  const { user, token } = useAuth();
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noticeId: string) => {
      if (!token || !user?.schoolId) throw new Error("No token");
      return api.notices.delete(noticeId, user.schoolId, token);
    },
    onSuccess: (_result, noticeId) => {
      dropDeletedNotices(queryClient, [noticeId]);
      refreshAfterPlannerEvents(queryClient);
      showMessage({ message: "Event deleted", severity: "success" });
    },
    onError: (error) => {
      console.error("Failed to delete the Event:", error);
      showMessage({
        message: "Couldn't delete the Event." + failureDetail(error),
        severity: "error",
      });
    },
  });
};

/** Shows a changed Event's new when and where in every cached Notices list straight away; the refetch confirms it. */
const showChangedNotice = (queryClient: QueryClient, changed: Notice) => {
  const changedId = changed.noticeId || changed.id;
  const whenAndWhere = {
    eventStartDate: changed.eventStartDate,
    eventStartTime: changed.eventStartTime,
    eventEndDate: changed.eventEndDate,
    eventEndTime: changed.eventEndTime,
    eventLocation: changed.eventLocation,
  };
  queryClient.setQueriesData({ queryKey: ["notices"] }, (cached: unknown) =>
    Array.isArray(cached)
      ? cached.map((notice: Notice) =>
          (notice.noticeId || notice.id) === changedId ? { ...notice, ...whenAndWhere } : notice,
        )
      : cached,
  );
};

/**
 * Moves a sent Planner's Event to a new date, time or location. Its Event
 * reminders move with it; "Let parents know" also notifies parents. The
 * dialog shows a failure itself.
 */
export const useChangePlannerEvent = () => {
  const { user, token } = useAuth();
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      plannerId,
      noticeId,
      change,
      letParentsKnow,
    }: {
      plannerId: string;
      noticeId: string;
      change: PlannerEventWhenAndWhere;
      letParentsKnow: boolean;
    }) => {
      if (!token || !user?.schoolId) throw new Error("No token");
      return api.planners.changePlannerEvent(
        user.schoolId,
        plannerId,
        noticeId,
        { ...change, letParentsKnow },
        token,
      );
    },
    onSuccess: (result, { letParentsKnow }) => {
      showChangedNotice(queryClient, result.notice);
      refreshAfterPlannerEvents(queryClient);
      showMessage({
        message: result.changed ? changedPlannerEventMessage(letParentsKnow) : "Nothing changed.",
        severity: "success",
      });
    },
    onError: (error) => console.error("Failed to change the Event:", error),
  });
};

/** After Events are created or deleted: the Planners, Notices (Calendar, Upcoming, the Planner's list) and stats. */
const refreshAfterPlannerEvents = (queryClient: QueryClient) => {
  void queryClient.invalidateQueries({ queryKey: PLANNERS_QUERY_KEY });
  void queryClient.invalidateQueries({ queryKey: ["notices"] });
  void queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
};

/** Problems the server found on a Planner's rows when sending it, by row ID. */
export const sendPlannerRowProblems = (error: unknown): Record<string, PlannerRowProblems> =>
  plannerRowProblemsFromServer((error as ApiRequestError | null)?.responseBody);

/**
 * Saves the Draft as it is on screen, then sends it, so the Planner keeps
 * exactly the rows that went out. A Planner already Sending (a send that didn't
 * finish) can't be changed, so it's sent again as it is; the server only adds
 * the missing Events. Refreshes the Planners, Notices (so the Calendar shows
 * the new Events) and the dashboard stats.
 */
export const useSendPlanner = () => {
  const { user, token } = useAuth();
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      plannerId,
      draft,
      finishing,
      onSaved,
      driveFolderKey,
    }: {
      /** Missing until the first save. */ plannerId?: string;
      draft: PlannerDraft;
      /** The Planner is Sending: finish that send rather than saving first. */
      finishing?: boolean;
      /** Called once the Draft is saved, before sending, so a retry doesn't save a second one. */
      onSaved?: (plannerId: string) => void;
      /** Where attachments are filed in Drive (usePlannerDriveFolderKey). */
      driveFolderKey?: string;
    }) => {
      if (!token || !user?.schoolId) throw new Error("No token");
      let sendingId = plannerId;
      if (!finishing || !sendingId) {
        const saved = sendingId
          ? await api.planners.updatePlanner(user.schoolId, sendingId, draft, token)
          : await api.planners.createPlanner(user.schoolId, draft, token);
        sendingId = saved.planner.plannerId;
        onSaved?.(sendingId);
      }
      const events = plannerEventPayloads(draft, draft.rows, {
        defaultDriveFolderKey: driveFolderKey,
      });
      const response = await api.planners.sendPlanner(user.schoolId, sendingId, events, token);
      return response.planner;
    },
    onSettled: () => refreshAfterPlannerEvents(queryClient),
    onError: (error) => {
      console.error("Failed to send Planner:", error);
      showMessage({ message: plannerSendFailureMessage(error), severity: "error" });
    },
  });
};

/**
 * Adds an Event to a sent Planner. Refreshes the Planners, Notices (so it shows
 * in the Planner, on the Calendar and in Upcoming) and the dashboard stats.
 */
export const useAddPlannerEvent = () => {
  const { user, token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      planner,
      row,
      letParentsKnow,
      driveFolderKey,
    }: {
      planner: SavedPlanner;
      row: PlannerRow;
      letParentsKnow: boolean;
      /** Where attachments are filed in Drive (usePlannerDriveFolderKey). */
      driveFolderKey?: string;
    }) => {
      if (!token || !user?.schoolId) throw new Error("No token");
      const request = plannerAddedEventRequest(planner, row, {
        letParentsKnow,
        defaultDriveFolderKey: driveFolderKey,
      });
      return api.planners.addPlannerEvent(user.schoolId, planner.plannerId, request, token);
    },
    onSettled: () => refreshAfterPlannerEvents(queryClient),
    // The dialog shows the failure (plannerAddEventFailureMessage) on its result screen.
    onError: (error) => console.error("Failed to add an Event to the Planner:", error),
  });
};
